/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import { motion, Transition, useAnimation, useInView, Variant } from "motion/react";
import { forwardRef, useEffect, useRef, useSyncExternalStore } from "react";
import { useMergedRefs } from "@components/utils/mergeRefs";
import { css, cx } from "@panda/css";
import { SystemStyleObject } from "@panda/types";
import { useIsRtl } from "@components/hooks";
import { useCosmosComponentContext } from "@components/context/ComponentContext";
import { ANIMATION } from "@components/constants";

const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

function subscribeToReducedMotion(callback: () => void): () => void {
	const mql = window.matchMedia(REDUCED_MOTION_QUERY);
	mql.addEventListener("change", callback);
	return () => mql.removeEventListener("change", callback);
}

function getReducedMotionSnapshot(): boolean {
	return window.matchMedia(REDUCED_MOTION_QUERY).matches;
}

function getServerSnapshot(): boolean {
	return false;
}

function useReducedMotion(): boolean {
	return useSyncExternalStore(subscribeToReducedMotion, getReducedMotionSnapshot, getServerSnapshot);
}

export type AnimateProps = {
	children: React.ReactNode;
	delay?: number;
	type?: "fade" | "slide" | "slideLeft" | "slideRight";
	distance?: string;
	customCss?: SystemStyleObject;
};

interface AnimationVariant {
	[key: string]: {
		hidden: Variant;
		visible: Variant;
	};
}

export const Animate = forwardRef(
	(
		{ children, delay = 0, type = "fade", customCss, distance }: AnimateProps,
		forwardedRef: React.Ref<HTMLDivElement>,
	): React.JSX.Element => {
		const { disableAnimations = false } = useCosmosComponentContext();
		const prefersReducedMotion = useReducedMotion();
		const shouldDisable = disableAnimations || prefersReducedMotion;
		const ref = useRef<Element>(null);
		const mergedRef = useMergedRefs(ref, forwardedRef);

		const controls = useAnimation();
		const isInView = useInView(ref, {
			amount: 0,
			margin: "0px 0px -32px 0px",
			once: true,
		});
		const isRtl = useIsRtl();

		const transition: Transition = {
			ease: "easeOut",
			opacity: { duration: ANIMATION.duration.opacity, delay },
			y: { duration: ANIMATION.duration.move, delay },
			x: { duration: ANIMATION.duration.move, delay },
		};

		const animationVariants: AnimationVariant = {
			fade: {
				hidden: { opacity: 0.0001, transition },
				visible: { opacity: 1, transition },
			},
			slide: {
				hidden: { opacity: 0.0001, transition, y: distance || "1.75rem" },
				visible: { opacity: 1, transition, y: 0 },
			},
			slideLeft: {
				hidden: { opacity: 0.0001, transition, x: distance || "1rem" },
				visible: { opacity: 1, transition, x: 0 },
			},
			slideRight: {
				hidden: { opacity: 0.0001, transition, x: distance ? `-${distance}` : "-1.5rem" },
				visible: { opacity: 1, transition, x: 0 },
			},
		};

		useEffect(() => {
			if (shouldDisable) return;

			if (isInView) {
				controls.start("visible").catch((e) => {
					throw e;
				});
			}
		}, [controls, isInView, shouldDisable]);

		const finalType = isRtl && type === "slideLeft" ? "slideRight" : type;

		if (shouldDisable) {
			return (
				<div className={cx("animate", css(customCss))} ref={mergedRef}>
					{children}
				</div>
			);
		}

		return (
			<motion.div
				className={cx("animate", css(customCss))}
				animate={controls}
				initial="hidden"
				ref={mergedRef}
				variants={{
					hidden: animationVariants[finalType].hidden,
					visible: animationVariants[finalType].visible,
				}}
			>
				{children}
			</motion.div>
		);
	},
);

Animate.displayName = "Animate";
